/**
 * genome — the studio's ONE composed genome, EAGER ROUTE-ONLY phase (#705 S3).
 *
 * This is the module the studio ENTRY consumes: `main.tsx` composes it at boot and
 * `App.tsx` resolves the router's route table from it. Its job is ONLY to carry the
 * genome's ROUTE TABLE + each route's (lazy) editor — everything `resolveRoutes`
 * needs (it reads route `meta.path`/`meta.title`/`routes-to` and resolves the editor
 * via `plugin.editors[id]`; it NEVER looks up a cluster's schema/store/service entity).
 *
 * ## The generate-split (#705)
 *
 * The 8 GENERATED clusters (todo-app / host-app / agent-profiles / monzo /
 * home-battery / printer / slicer-profile / filament) are composed here as
 * schema-free {@link studioAppRoutePlugin}s from their hand-authored
 * {@link STUDIO_CLUSTER_DESCRIPTORS} — so NO `generate()`, NO schema literals, and
 * NO strand kernel land on the studio entry. Each route plugin emits only its route
 * entities (byte-identical to the full `studioAppPlugin`'s, the S2 parity gate) plus
 * routeless stub `component` targets (so the composed genome validates), and its
 * generated editors load behind `React.lazy` off the entry. The FULL 16-kind graph
 * (schemas + stores + services + behavioural kinds) is composed LAZILY in
 * `./genome-full.js`, consumed only OFF the entry (the genome-graph panel/PERCEIVE,
 * the agent catalog, restage/install, the census + gates).
 *
 * The hand-authored plugins (shell / dev / ide / agent / store / secrets / pipeline
 * / cad / device / printer-twin / home / scenes + the monzo/hb DERIVED tails) compose
 * with their FULL entities exactly as before — the split touches only the generated
 * CRUD clusters (whose heavy graph is the reclaim target).
 *
 * The `slicerPlugin` bespoke surface is imported from `../slicer/slicer-plugin.js`
 * DIRECTLY (not the `../slicer/index.js` family barrel, which composes the generated
 * slicer-profile/filament clusters with `studioAppPlugin` — that would drag
 * `generate()` back onto the entry).
 *
 * @module @ubiclouds/v5-studio/genome/genome
 */

import type { GenomeV3 } from '@ubiclouds/v5-sdk/genome';
import type { ComposableGenomePlugin } from '@ubiclouds/v5-sdk/plugins';
// The plugin-agnostic identity + compose helpers (entry-safe — no plugins, no
// generate()). Importing them from the SHARED module keeps `genome-full.js` (the
// full-graph compose) off this eager module's static graph.
import {
  STUDIO_GENOME_META,
  composeGenomeUnvalidated,
} from './studio-genome-compose.js';
// Granular `./plugin` subpath (NOT the barrel), the #226/#119 barrel-trap cure.
import { devPlugin } from '@ubiclouds/v5-genome-dev-plugin/plugin';
import { shellPlugin } from './shell-plugin.js';
import { studioAppRoutePlugin } from './studio-app-route-plugin.js';
import {
  printerCluster,
  todoAppCluster,
  hostAppCluster,
  agentProfilesCluster,
  monzoCluster,
  homeBatteryCluster,
  slicerProfileCluster,
  filamentCluster,
  budgetCluster,
  type StudioClusterDescriptor,
} from './studio-app-descriptors.js';
// #904: the eager entry composes the ROUTE-ONLY derived-tail plugins (route +
// component entities + lazy editors only) so the FULL plugins' behavioural entity
// literals (store/query/event/action/effect) stay OFF the entry chunk. The FULL
// plugins are composed in `genome-full.ts` (the lazy full-graph phase).
import { monzoDerivedRoutePlugin } from '@ubiclouds/v5-finance/routes';
import { hbDerivedRoutePlugin } from '@ubiclouds/v5-home-battery/hb-derived-routes';
import { budgetDerivedRoutePlugin } from './budget-derived-routes.js';
import { ideShellPlugin } from '../ide/ide-shell-plugin.js';
import { agentPlugin } from '../agent/agent-plugin.js';
import { storePlugin } from '@ubiclouds/v5-store-ui/store-plugin';
import { secretsPlugin } from '@ubiclouds/v5-vault/plugin';
import { pipelinePlugin } from '@ubiclouds/v5-pipeline/plugin';
import { cadPlugin } from '../cad/cad-plugin.js';
// The bespoke `/slicer` surface ONLY (NOT the `../slicer/index.js` family barrel,
// which eagerly composes the generated slicer-profile/filament `studioAppPlugin`s).
import { slicerPlugin } from '../slicer/slicer-plugin.js';
import { devicePlugin } from '../device/device-plugin.js';
import { printerTwinPlugin } from '../device/twin/printer-twin-plugin.js';
import { homePlugin } from '../home/home-plugin.js';
import { scenesPlugin } from '../home/scenes-plugin.js';
import { kioskPlugin } from '../kiosk/kiosk-plugin.js';

// Re-export the shared identity + the plugin-agnostic compose helpers so this
// module stays the entry's single genome import surface (main.tsx / host-url /
// the gate tests resolve them here). `studioGenome`/`studioPlugins`/
// `composeStudioGenome` below are this module's OWN route-only definitions.
export {
  STUDIO_GENOME_ID,
  STUDIO_GENOME_META,
  composeGenomeUnvalidated,
} from './studio-genome-compose.js';
// #865 S1 — the VALIDATING mouth lives in its own module (the entry chunk must
// materialise every symbol another chunk imports from a module it contains, so
// keeping the builder-backed compose here would drag `GenomeBuilderV3` onto the
// entry). Re-exported so `genome.js` stays the single genome import surface;
// nothing on the eager boot path calls these, so the re-export tree-shakes away.
export { composeGenome, composeStudioGenomeFrom } from './studio-genome-compose-validated.js';

/** Compose a generated cluster as its schema-free ROUTE-only entry plugin. */
function routePlugin(cluster: StudioClusterDescriptor): ComposableGenomePlugin {
  return studioAppRoutePlugin(cluster.descriptor, cluster.options);
}

/**
 * The plugins composed into the EAGER (route-only) studio genome, in adoption
 * order (high → low) — the SAME order the full compose (`genome-full.ts`) uses,
 * with each generated cluster's `studioAppPlugin(model, …)` swapped for the
 * schema-free `routePlugin(descriptor)`. The generated route table is byte-
 * identical to the full compose (the S2 parity gate + gate 4).
 */
export const studioPlugins: readonly ComposableGenomePlugin[] = Object.freeze([
  shellPlugin,
  devPlugin,
  ideShellPlugin,
  agentPlugin,
  storePlugin,
  secretsPlugin,
  pipelinePlugin,
  cadPlugin,
  devicePlugin,
  printerTwinPlugin,
  homePlugin,
  scenesPlugin,
  // #1391 — the touch-only wall-tablet consumption surface. Route-only, no dock, no
  // new EntityKind; the editor is React.lazy so the body stays off the eager entry.
  kioskPlugin,
  // #548-P0: the slicer family — the bespoke `/slicer` surface + its two GENERATED
  // clusters (slice-settings + filaments) as ROUTE-only plugins here (the full
  // `slicerPluginFamily` barrel composes them with `studioAppPlugin`, off-entry).
  slicerPlugin,
  routePlugin(slicerProfileCluster),
  routePlugin(filamentCluster),
  // The generated CRUD clusters, ROUTE-only (their full graphs live in genome-full.ts):
  routePlugin(printerCluster), // #513e — `/printer` connector (omitIndex).
  routePlugin(todoAppCluster), // §10 todo demonstrator, re-homed to `/app`.
  routePlugin(hostAppCluster), // #S-18c — Host 1:N Project CRUD under `/host-app`.
  routePlugin(agentProfilesCluster), // #363a — Gemma AgentProfile CRUD (omitIndex).
  routePlugin(monzoCluster), // #409 — banking CRUD under `/monzo`.
  // #409 bespoke tail: the hand-authored monzo DERIVED plugin — ROUTE-ONLY on the
  // entry (#904; the FULL behavioural graph lives in genome-full.ts).
  monzoDerivedRoutePlugin,
  routePlugin(homeBatteryCluster), // #426 — parts-catalog CRUD under `/home-battery`.
  // #426 bespoke tail: the hand-authored home-battery DERIVED plugin — ROUTE-ONLY on
  // the entry (#904; the FULL behavioural graph lives in genome-full.ts).
  hbDerivedRoutePlugin,
  // #1392 S1 — the generated budget baseline CRUD core under `/budget`.
  routePlugin(budgetCluster),
  // #1392 S2 (#1600) bespoke tail: the hand-authored budget DERIVED plugin —
  // ROUTE-ONLY on the entry (#904); the FULL behavioural graph (store + six
  // `query`s carrying real `$expr` folds) lives in genome-full.ts, so
  // `createQueryRuntime` never reaches the eager entry chunk (gate 8).
  budgetDerivedRoutePlugin,
]);

/**
 * Compose THE eager (route-only) studio genome from the frozen `studioPlugins`
 * base. Uses the NO-VALIDATE build (#518, gate 8) so `./validate.js` stays off the
 * entry chunk — the module-scope `studioGenome` below is composed here. Boot safety
 * is the `main.tsx` ErrorBoundary + the validating restage/install paths + the
 * validating-build assertion pinned in `genome.test.ts` / `one-genome.test.ts`.
 */
export function composeStudioGenome(): GenomeV3 {
  return composeGenomeUnvalidated(STUDIO_GENOME_META, studioPlugins);
}

/**
 * THE eager studio genome — composed ONCE at module scope (the one-genome
 * invariant). The app boot + router consume this single instance; it carries the
 * full route table with the generated clusters as route + stub-`component` entities
 * (the full generated graph is in `genome-full.ts`, lazy off the entry).
 */
export const studioGenome: GenomeV3 = composeStudioGenome();

/**
 * The SAME instance under a name that says which corpus it is (#1851).
 *
 * ── WHY AN ALIAS AND NOT A RENAME ──
 *
 * Both this module and `./genome-full.ts` export a binding called `studioGenome`, and
 * they describe different applications: 115 entities / 3 kinds / 0 declared containers
 * / 0 genome ops here, against 554 / 13 / 2 / 122 there. That collision is what makes a
 * wrong import invisible AT THE CALL SITE — two measured instances of a shipped, green
 * assertion about a corpus the user never sees (`capability-surface-integration.test.ts`
 * and `containment-reflow-measured.test.ts`).
 *
 * The rename is REJECTED on measurement, and the reason is recorded here rather than
 * only on the board. Blast radius measured when #1851 started: 40 boot / 62 full = 102
 * files (35 / 67 after its corrections — re-derive, never quote). Renaming the FULL
 * one breaks the PUBLIC `@ubiclouds/v5-studio` barrel export that the census, the gate
 * suites and `scripts/studio-routes.ts` consume — and the full corpus does not need a
 * new name, because **the barrel already IS one**: `./index.ts` re-exports
 * `studioGenome` from `genome-full.js` ONLY, so reaching it through the package
 * specifier is already unambiguous. It is the BOOT corpus that had no distinguished
 * name. `genome-full.ts` is also 499/500 against gate 7 with its doctrine-correct
 * extraction BLOCKED (`scripts/studio-plugin-packaging.ts` hardcodes `COMPOSITION_ROOT`
 * and reads the array literal out of that file), so even a one-line alias there would
 * land it exactly at the ceiling with zero headroom. It is touched ZERO times.
 *
 * ⚠ AND NAMING IS NOT THE FIX, which is why this is additive and nothing is migrated
 * onto it. 14 files import BOTH corpora and EVERY ONE of them already aliases
 * (`routerGenome`, `bootGenome`, `eagerGenome`, `routeOnlyGenome`, `fullGenome`…): the
 * authors who knew they needed both disambiguated voluntarily and got it right. The
 * mistake is made at the CONSUMING CALL by an author who did not know there were two,
 * and what catches that is `scripts/__tests__/genome-corpus-choice.test.ts`, not a
 * better name. This alias exists so a deliberate boot binding can SAY SO.
 *
 * Zero cost: it is a re-export of the same frozen instance, not a second compose.
 */
export const bootStudioGenome: GenomeV3 = studioGenome;
